import React, { createContext, useContext, ReactNode, useState, useCallback, useMemo } from 'react';
import { SignupReason } from './SignupCardV2';
import { BaseCardProps, getRecaptchaToken } from '../../shared/shared';
import { SignupApi } from '../../../../api/signupApi';
import { getIsoCode } from '../../../../utils/configurations';
import { ensureExists, ensureUnreachable } from '@signup/shared/src/utils/ensure';
import { SignupFormFields, useSignupFormData } from './SignupFormDataContext';
import {
  Cloud,
  countriesKeyedByIsoCode,
  isSuppressionErrorCode,
  SignupApiErrorCode,
  SignupEventType,
  validateCompanyNameWithReason,
  validateInputWithReason,
  validatePhoneNumberWithReason,
  isCortexCodeFlow,
} from '@signup/shared';
import { useEmailValidator } from '../../utils/validationUtils';
import { useTelemetry, ValidationResponse } from '@snowflake/core-ui';
import { useDeploymentMetadata } from '../../utils/deploymentUtils';
import { RegionSelect } from '../../../../models';
import { isCountryISOCodeOptIn, useIsPhoneNumberRequired } from '../../utils/SignupCountryUtils';
import { useIntl } from 'react-intl';
import { SignupEventName } from '../../../../utils/SignupLogger';
import * as configs from '../../../../utils/configurations';
import { SignupParams } from '../../../../utils/ParseQueryParams';
import { useSignupPageContext } from '../../../../pages/SignupContext';
import { MutinyExperience } from '../../../../utils/MutinyExperience';
import {
  createRadarSessionForSignup,
  redactRadarSessionForLog,
} from '../../../../utils/stripeRadarSession';
import { getAwsMarketplaceType } from '../../../../utils/signupFlowTypeUtils';
import { getBotDetectionProps } from '../../../../utils/botDetection';
import { useSignupFlowType } from '../../../../utils/useSignupFlowType';

// Define the shape of the context state
export interface SignupFormControllerState {
  submitSignupForm: (params: { formTwoCompletionTime: number }) => Promise<void>;
  submitWaitlistForm: () => Promise<void>;
  accountCreationFinished: boolean;
  waitlistEntryFinished: boolean;
  regionSelectList: RegionSelect;
  regionSelectListSortedByDistance: boolean;
  validateField: (
    fieldName: SignupFormFields,
    value: string | boolean | null,
  ) => ValidationResponse;
  signupCardProps: BaseCardProps;
  creationErrorCode: SignupApiErrorCode | null;
  waitlistErrorCode: SignupApiErrorCode | null;
  clearCreationErrorAndResetRecaptcha: () => void;
  clearWaitlistErrorAndResetRecaptcha: () => void;
  showVisibleRegionsFilter: boolean;
}

// Create the context
const SignupFormControllerContext = createContext<SignupFormControllerState | undefined>(undefined);

function useSignupFormInitialization() {
  const { setField } = useSignupFormData();
  const { modifiedSignupFlowExperimentGroup } = useSignupPageContext();

  React.useEffect(() => {
    const isModifiedFlow = modifiedSignupFlowExperimentGroup === 'MODIFIED_FLOW_TREATMENT';

    if (!isModifiedFlow) {
      const ipIsoCode = getIsoCode();
      const matchedIsoCode: string =
        ipIsoCode && countriesKeyedByIsoCode[ipIsoCode] ? ipIsoCode : '';
      setField('countryIsoCode', matchedIsoCode);
    }
  }, [setField]);
}

function useEmailAgreementStateListener() {
  const { countryIsoCode, setField } = useSignupFormData();
  const { renderingOptions } = useSignupPageContext();

  React.useEffect(() => {
    if (renderingOptions.showEmailAgreementCheckbox) {
      setField('givenEmailOptInChoice', true);
      return;
    }
    setField('givenEmailOptInChoice', isCountryISOCodeOptIn(countryIsoCode));
  }, [countryIsoCode, setField, renderingOptions.showEmailAgreementCheckbox]);
}

// Append a suffix to the school name for student signups, but also limit the length to 39 characters
// This is to make the school names easier to identify in SFDC by human beings, so that a student of
// UC Berkeley could be treated different from a faulty.
// It's not a problem if suffix is not added.
export function decorateSchoolName(schoolName: string): string {
  return `${schoolName} - Student`.slice(0, 39);
}

function useSubmitWaitlistForm(props: BaseCardProps) {
  const { marketingParams, formId, recaptchaRef } = props;
  const formState = useSignupFormData();
  const [waitlistEntryFinished, setWaitlistEntryFinished] = useState(false);
  const [waitlistErrorCode, setWaitlistErrorCode] = useState<SignupApiErrorCode | null>(null);
  const { logAction, logEvent } = useTelemetry();

  const submitWaitlistForm = useCallback(async () => {
    const recaptchaToken = await getRecaptchaToken(recaptchaRef);

    // Opt-out is no longer shown in any country
    const finalOptOutEmailAgreement = undefined;

    // Prepare waitlist-specific fields (subset of signup fields)
    const options = {
      firstName: formState.firstName.trim(),
      lastName: formState.lastName.trim(),
      email: formState.email.trim().toLowerCase(),
      country: countriesKeyedByIsoCode[formState.countryIsoCode]?.value || '',
      recaptchaToken,
      optInEmailAgreement: formState.optInEmailAgreement,
      optOutEmailAgreement: finalOptOutEmailAgreement,
      givenEmailOptInChoice: formState.givenEmailOptInChoice,
      ...marketingParams,
      formId: formId,
    };

    logAction(
      SignupEventName.CORTEX_CODE_WAITLIST_FORM_REQUEST_STARTED,
      'ui_click',
      'CortexCodeWaitListCard',
      {
        ...options,
        ...(options.recaptchaToken && { recaptchaToken: 'REDACTED' }),
        referrerUrl: configs.getReferrerUrl(),
      },
    );

    const requestStartTime = Date.now() / 1000;

    try {
      const { res, headers } = await SignupApi.joinWaitlist(options);
      const txnId = headers.get('X-TXN-ID') || 'unknown';

      if (res.success !== true) {
        setWaitlistErrorCode(res.errorCode);

        if (isSuppressionErrorCode(res.errorCode)) {
          // Log suppression
          logEvent({
            event: SignupEventName.CORTEX_CODE_WAITLIST_FORM_SUBMIT_SUPPRESSED,
            type: SignupEventType.UI_RESPONSE_SUCCESS,
            data: {
              waitlistResponseDuration: Date.now() / 1000 - requestStartTime,
              suppressionReason: res.errorCode,
              errorCode: res.errorCode,
              txnId,
              flowType: 'cortex_code_waitlist',
            },
            interaction: false,
          });
        } else {
          // Log error with detailed information
          logEvent({
            event: SignupEventName.CORTEX_CODE_WAITLIST_FORM_SUBMIT_FAILURE,
            type: SignupEventType.UI_RESPONSE_ERROR,
            data: {
              waitlistResponseDuration: Date.now() / 1000 - requestStartTime,
              txnId,
              errorCode: res.errorCode,
              errorType: 'api_error',
              email: options.email,
              country: options.country,
            },
            interaction: false,
          });
        }
      } else {
        // Success case - only log success if actually successful
        logEvent({
          event: SignupEventName.CORTEX_CODE_WAITLIST_FORM_SUBMIT_SUCCESS,
          type: SignupEventType.UI_RESPONSE_SUCCESS,
          data: {
            waitlistResponseDuration: Date.now() / 1000 - requestStartTime,
            txnId,
            email: options.email,
            country: options.country,
          },
          interaction: false,
        });
      }
    } catch (err) {
      setWaitlistErrorCode(SignupApiErrorCode.GENERIC_ERROR);

      // Log exception error with detailed information
      logEvent({
        event: SignupEventName.CORTEX_CODE_WAITLIST_FORM_SUBMIT_FAILURE,
        type: SignupEventType.UI_RESPONSE_ERROR,
        data: {
          waitlistResponseDuration: Date.now() / 1000 - requestStartTime,
          txnId: 'null',
          errorCode: SignupApiErrorCode.GENERIC_ERROR,
          errorType: 'exception',
          errorMessage: (err as Error).message || 'Unknown error',
          email: options.email,
          country: options.country,
        },
        interaction: false,
      });
    } finally {
      setWaitlistEntryFinished(true);
    }
  }, [formState, marketingParams, formId, recaptchaRef, logAction, logEvent]);

  const clearWaitlistErrorAndResetRecaptcha = useCallback(() => {
    setWaitlistErrorCode(null);
    setWaitlistEntryFinished(false);
    recaptchaRef.current?.reset();
  }, [recaptchaRef]);

  return {
    submitWaitlistForm,
    waitlistEntryFinished,
    waitlistErrorCode,
    clearWaitlistErrorAndResetRecaptcha,
  };
}

function useSubmitSignupForm(props: BaseCardProps) {
  const { signupParams, marketingParams, formId, recaptchaRef } = props;
  const awsType = getAwsMarketplaceType();
  const formState = useSignupFormData();
  const [accountCreationFinished, setAccountCreationFinished] = useState(false);
  const [creationErrorCode, setCreationErrorCode] = useState<SignupApiErrorCode | null>(null);
  const { logAction, logEvent } = useTelemetry();
  const isPhoneNumberRequired = useIsPhoneNumberRequired();
  const { modifiedSignupFlowExperimentGroup } = useSignupPageContext();

  const submitSignupForm = useCallback(
    async ({ formTwoCompletionTime }: { formTwoCompletionTime: number }) => {
      const recaptchaToken = await getRecaptchaToken(recaptchaRef);
      const radarSession = await createRadarSessionForSignup();

      // Opt-out is no longer shown in any country
      const finalOptOutEmailAgreement = undefined;

      // Wait for Mutiny experience data
      const mutinyExperienceData = await MutinyExperience.waitForMutinyData();

      const options = SignupApi.prepareSignupFields({
        firstName: formState.firstName,
        lastName: formState.lastName,
        email: formState.email,
        countryIsoCode: formState.countryIsoCode,
        company:
          formState.signupReason === SignupReason.Student
            ? decorateSchoolName(formState.schoolName)
            : formState.companyName,
        phoneNumber: isPhoneNumberRequired ? formState.phoneNumber : undefined,
        // Student signup no longer requires a job title. Fill in student in this case.
        role: formState.signupReason === SignupReason.Student ? 'Student' : formState.jobTitle,
        edition: formState.edition,
        cloud: ensureExists(formState.cloud),
        region: formState.region,
        awsType,
        signupParams,
        marketingParams,
        formId,
        recaptchaToken,
        formTwoCompletionTime,
        optInEmailAgreement: formState.optInEmailAgreement,
        optOutEmailAgreement: finalOptOutEmailAgreement,
        givenEmailOptInChoice: formState.givenEmailOptInChoice,
        mutinyExperienceData,
        radarSession,
      });

      logAction(SignupEventName.SIGNUP_FORM_TWO_COMPLETE, 'ui_click', 'SignupCard', {
        ...options,
        ...(options.recaptchaToken && { recaptchaToken: 'REDACTED' }),
        ...(options.radarSession && {
          radarSession: redactRadarSessionForLog(options.radarSession),
        }),
        ...(options.ampt && { ampt: 'REDACTED' }),
        referrerUrl: configs.getReferrerUrl(),
      });
      logAction(SignupEventName.SIGNUP_BOT_DETECTION, 'ui_click', 'SignupCard', {
        ...getBotDetectionProps(),
        trigger: 'form_submit',
      });

      const requestStartTime = Date.now() / 1000;

      try {
        const { res, headers } = await SignupApi.createTrial(options);
        const txnId = headers.get('X-TXN-ID') || 'unknown';
        const isModifiedFlow = modifiedSignupFlowExperimentGroup === 'MODIFIED_FLOW_TREATMENT';

        if (res.success !== true) {
          setCreationErrorCode(res.errorCode);

          if (isSuppressionErrorCode(res.errorCode)) {
            // Log suppression
            logEvent({
              event: isModifiedFlow
                ? SignupEventName.SIGNUP_MODIFIED_SUPPRESSED
                : SignupEventName.SIGNUP_SUPPRESSED,
              type: SignupEventType.UI_RESPONSE_SUCCESS,
              data: {
                thankYouResponseDuration: Date.now() / 1000 - requestStartTime,
                suppressionReason: res.errorCode,
                errorCode: res.errorCode,
                txnId,
                flowType: isModifiedFlow ? 'modified' : 'control',
              },
              interaction: false,
            });
          } else {
            // Log error with detailed information
            logEvent({
              event: isModifiedFlow
                ? SignupEventName.SIGNUP_MODIFIED_ERROR
                : SignupEventName.SIGNUP_ACCOUNT_CREATION_ERROR,
              type: SignupEventType.UI_RESPONSE_ERROR,
              data: {
                thankYouResponseDuration: Date.now() / 1000 - requestStartTime,
                txnId,
                errorCode: res.errorCode,
                errorType: 'api_error',
                email: options.email,
                cloud: options.cloud,
                region: options.region,
                edition: options.edition,
                country: options.country,
                flowType: isModifiedFlow ? 'modified' : 'control',
              },
              interaction: false,
            });

            // Log that try again page will be shown
            if (isModifiedFlow) {
              logEvent({
                event: SignupEventName.SIGNUP_MODIFIED_TRY_AGAIN_PAGE_SHOWN,
                type: SignupEventType.UI_RESPONSE_ERROR,
                data: {
                  errorCode: res.errorCode,
                  txnId,
                  flowType: 'modified',
                },
                interaction: false,
              });
            }
          }
        } else {
          // Success case - only log success if actually successful
          logEvent({
            event: isModifiedFlow
              ? SignupEventName.SIGNUP_MODIFIED_SUCCESS
              : SignupEventName.SIGNUP_SUCCESS,
            type: SignupEventType.UI_RESPONSE_SUCCESS,
            data: {
              thankYouResponseDuration: Date.now() / 1000 - requestStartTime,
              txnId,
              email: options.email,
              cloud: options.cloud,
              region: options.region,
              edition: options.edition,
              country: options.country,
              flowType: isModifiedFlow ? 'modified' : 'control',
            },
            interaction: false,
          });

          // Reset after submission so a fresh token can be generated next time
          recaptchaRef.current?.reset();
        }
      } catch (err) {
        setCreationErrorCode(SignupApiErrorCode.GENERIC_ERROR);

        const isModifiedFlow = modifiedSignupFlowExperimentGroup === 'MODIFIED_FLOW_TREATMENT';

        // Log exception error with detailed information
        logEvent({
          event: isModifiedFlow
            ? SignupEventName.SIGNUP_MODIFIED_ERROR
            : SignupEventName.SIGNUP_ACCOUNT_CREATION_ERROR,
          type: SignupEventType.UI_RESPONSE_ERROR,
          data: {
            thankYouResponseDuration: Date.now() / 1000 - requestStartTime,
            txnId: 'null',
            errorCode: SignupApiErrorCode.GENERIC_ERROR,
            errorType: 'exception',
            errorMessage: (err as Error).message || 'Unknown error',
            email: options.email,
            cloud: options.cloud,
            region: options.region,
            edition: options.edition,
            country: options.country,
            flowType: isModifiedFlow ? 'modified' : 'control',
          },
          interaction: false,
        });

        // Log that try again page will be shown
        if (isModifiedFlow) {
          logEvent({
            event: SignupEventName.SIGNUP_MODIFIED_TRY_AGAIN_PAGE_SHOWN,
            type: SignupEventType.UI_RESPONSE_ERROR,
            data: {
              errorCode: SignupApiErrorCode.GENERIC_ERROR,
              errorType: 'exception',
              txnId: 'null',
              flowType: 'modified',
            },
            interaction: false,
          });
        }
      } finally {
        setAccountCreationFinished(true);
      }
    },
    [
      formState,
      awsType,
      signupParams,
      marketingParams,
      formId,
      recaptchaRef,
      logAction,
      logEvent,
      isPhoneNumberRequired,
      modifiedSignupFlowExperimentGroup,
    ],
  );

  const clearCreationErrorAndResetRecaptcha = useCallback(() => {
    setCreationErrorCode(null);
    setAccountCreationFinished(false);
    recaptchaRef.current?.reset();
  }, [setCreationErrorCode, setAccountCreationFinished, recaptchaRef]);

  return {
    submitSignupForm,
    accountCreationFinished,
    creationErrorCode,
    clearCreationErrorAndResetRecaptcha,
  };
}

function useValidateField() {
  const { validateEmailHelper } = useEmailValidator();
  const { signupReason, countryIsoCode } = useSignupFormData();
  const { formatMessage } = useIntl();

  const validateStringInput = useCallback(
    (value: boolean | string | null): string | true => {
      const result = typeof value === 'string' ? validateInputWithReason(value) : 'invalid';
      switch (result) {
        case 'empty':
          return formatMessage({ id: 'This field is required' });
        case 'invalid':
          return formatMessage({ id: 'Enter a different value' });
        case 'invalid_characters':
          return formatMessage({ id: 'Unsupported characters' });
        case 'success':
          return true;
        case 'longer_than_40':
          return formatMessage({ id: 'Enter a value shorter than 40 characters' });
        default:
          ensureUnreachable(result);
      }
    },
    [formatMessage],
  );

  const validateCompanyNameInput = useCallback(
    (value: boolean | string | null): string | true => {
      const result = typeof value === 'string' ? validateCompanyNameWithReason(value) : 'invalid';
      switch (result) {
        case 'empty':
          return formatMessage({ id: 'This field is required' });
        case 'invalid':
          return formatMessage({ id: 'Enter a different value' });
        case 'invalid_characters':
          return formatMessage({ id: 'Unsupported characters' });
        case 'success':
          return true;
        case 'longer_than_40':
          return formatMessage({ id: 'Enter a value shorter than 40 characters' });
        default:
          ensureUnreachable(result);
      }
    },
    [formatMessage],
  );

  const validatePhoneNumberInput = useCallback(
    (value: boolean | string | null): string | true => {
      if (countryIsoCode !== 'JP') {
        return true;
      }

      const result = typeof value === 'string' ? validatePhoneNumberWithReason(value) : 'empty';
      switch (result) {
        case 'empty':
          return formatMessage({ id: 'This field is required' });
        case 'invalid_characters':
          return formatMessage({ id: 'Unsupported characters' });
        case 'success':
          return true;
        case 'longer_than_40':
          return formatMessage({ id: 'Enter a value shorter than 40 characters' });
        default:
          ensureUnreachable(result);
      }
    },
    [formatMessage, countryIsoCode],
  );

  const validateExistence = useCallback(
    (value: boolean | string | null): string | true => {
      return !!value ? true : formatMessage({ id: 'This field is required' });
    },
    [formatMessage],
  );

  return useCallback(
    (fieldName: SignupFormFields, value: boolean | string | null): ValidationResponse => {
      // TODO(GROW-4457): get validation error copies for all scenarios.
      switch (fieldName) {
        // form one
        case 'firstName':
          return validateStringInput(value);
        case 'lastName':
          return validateStringInput(value);
        case 'email':
          return typeof value === 'string' && validateEmailHelper(value);
        case 'countryIsoCode':
          return !!value; // use custom ui for error message.
        case 'signupReason':
          return validateExistence(value);
        case 'optInEmailAgreement':
          return true; // always valid
        case 'optOutEmailAgreement':
          return true; // always valid
        case 'givenEmailOptInChoice':
          return true; // always valid
        case 'phoneNumber':
          return validatePhoneNumberInput(value);

        // form two
        case 'companyName':
          return signupReason === SignupReason.Student || validateCompanyNameInput(value);
        case 'jobTitle':
          return signupReason === SignupReason.Student || validateStringInput(value);
        case 'region':
          return validateExistence(value);
        case 'cloud':
          return !!value; // use custom ui for error message.
        case 'edition':
          return validateExistence(value);
        case 'schoolName':
          // yes, use company name validation for school name
          return signupReason !== SignupReason.Student || validateCompanyNameInput(value);
        case 'termsAgreement':
          return !!value; // use custom ui for error message.
        case 'filterVisibleRegions':
          return true; // always valid
        default:
          ensureUnreachable(fieldName);
      }
    },
    [
      validateEmailHelper,
      signupReason,
      validateStringInput,
      validateCompanyNameInput,
      validateExistence,
      validatePhoneNumberInput,
    ],
  );
}

function useDefaultRegionAutoFill(
  signupParams: SignupParams,
  regionSelectList: RegionSelect,
  regionSelectListSortedByDistance: boolean,
) {
  const { cloud, region, setField } = useSignupFormData();

  React.useEffect(() => {
    if (!cloud || !regionSelectListSortedByDistance) {
      return;
    }

    // If the region is determined in the url, the logic in this hook does not need to run
    if (signupParams.region || signupParams.regionp) {
      return;
    }

    let suggestedRegion = '';

    const regionList = regionSelectList[cloud];

    if (regionList.some(regionMeatadata => regionMeatadata.value === region)) {
      // current selected region is already on the cloud.
      return;
    }

    // We only suggest a region if we know where the user is (coordinates exist)
    // Picking the first entry for the cloud provider's available regions should be the closest since this list should be sorted.
    // If the cloud provider has no visible regions then the first entry will have a disabled flag set to true, in which case we set a default of ''
    // A cloud should always have an available region, but just in case this value is undefined we set a default of ''
    if (configs.getCoordinates() && regionList?.length && !regionList[0].disabled) {
      suggestedRegion = regionList[0].value ?? '';
    }

    setField('region', suggestedRegion);
  }, [cloud, setField, regionSelectList, signupParams, region, regionSelectListSortedByDistance]);
}

// Auto-select AWS for Cortex Code flows (CortexCodeCLI and CocoSubscriptionPromo) since it's the
// only supported cloud provider
function useCortexCodeCloudAutoSelect(signupParams: SignupParams) {
  const signupFlowType = useSignupFlowType();
  const { cloud, setField } = useSignupFormData();

  React.useEffect(() => {
    // If cloud is locked via URL parameter, don't override
    if (signupParams.cloud || signupParams.cloudp) {
      return;
    }

    // For Cortex Code flows, ensure AWS is selected since it's the only option
    if (isCortexCodeFlow(signupFlowType) && cloud !== Cloud.AWS) {
      setField('cloud', Cloud.AWS);
    }
  }, [signupFlowType, cloud, setField, signupParams.cloud, signupParams.cloudp]);
}

// Create the provider component
const SignupFormControllerProvider: React.FC<{ children: ReactNode } & BaseCardProps> = props => {
  const { children } = props;
  const { email, filterVisibleRegions } = useSignupFormData();

  useSignupFormInitialization();
  useEmailAgreementStateListener();

  const { regionSelectList, regionSelectListSortedByDistance, showVisibleRegionsFilter } =
    useDeploymentMetadata({
      props,
      email,
      filterVisibleRegions,
    });

  useCortexCodeCloudAutoSelect(props.signupParams);
  useDefaultRegionAutoFill(props.signupParams, regionSelectList, regionSelectListSortedByDistance);

  const {
    submitSignupForm,
    accountCreationFinished,
    creationErrorCode,
    clearCreationErrorAndResetRecaptcha,
  } = useSubmitSignupForm(props);
  const {
    submitWaitlistForm,
    waitlistEntryFinished,
    waitlistErrorCode,
    clearWaitlistErrorAndResetRecaptcha,
  } = useSubmitWaitlistForm(props);
  const validateField = useValidateField();
  const signupCardProps = useMemo(() => {
    const { children: _, ...extra } = props;
    return extra;
  }, [props]);

  return (
    <SignupFormControllerContext.Provider
      value={{
        submitSignupForm,
        submitWaitlistForm,
        accountCreationFinished,
        waitlistEntryFinished,
        validateField,
        regionSelectList,
        signupCardProps,
        regionSelectListSortedByDistance,
        creationErrorCode,
        waitlistErrorCode,
        clearCreationErrorAndResetRecaptcha,
        clearWaitlistErrorAndResetRecaptcha,
        showVisibleRegionsFilter,
      }}
    >
      {children}
    </SignupFormControllerContext.Provider>
  );
};

// Custom hook for using the context
const useSignupFormController = () => {
  const context = useContext(SignupFormControllerContext);
  if (context === undefined) {
    throw new Error('useSignupFormController must be used within a SignupFormControllerProvider');
  }
  return context;
};

export { SignupFormControllerProvider, useSignupFormController };
